import type { Phase, Regularity } from '../../shared/predict'
import type { Flow, Protection, SymptomKey } from '../../shared/types'

/**
 * 中文文案。这份是「母版」：其余语言的字典都以 `typeof zh` 为类型，
 * 少一个 key 编译就过不去，不会出现某个界面半中半英的情况。
 *
 * 组件里一律不写死文案，全部从这里取（见 index.ts 的 useI18n）。
 */
export const zh = {
  /** 和语言绑死的格式约定，散落在各处的 Intl 调用都从这里取 */
  meta: {
    /** BCP 47 标签，喂给 Intl */
    tag: 'zh-CN',
    /** 0 = 周日起，1 = 周一起。中文习惯周一 */
    weekStart: 1 as 0 | 1,
    /** 并列词之间的分隔符 */
    listSep: '、',
  },

  /** 语言切换器里显示的名字，永远用该语言自己的说法，不翻译 */
  languageName: '中文',

  appName: 'Moonly',

  nav: {
    home: '今天',
    calendar: '日历',
    stats: '统计',
    us: '我们',
    settings: '设置',
  },

  login: {
    title: '欢迎回来',
    subtitle: '你的记录是私密的，只有你和你邀请的人能看到',
    name: '登录名',
    passphrase: '口令',
    submit: '登录',
    working: '正在验证…',
    hint: '第一次登录会稍等一两秒，是在本机做口令加密',
    toSignup: '还没有账号？注册一个',
    toJoin: '收到了邀请码？点这里加入',
    toLogin: '已经有账号了？去登录',
    toForgot: '忘记口令了？',
  },

  forgot: {
    title: '找回口令',
    subtitle: '把重置链接发到你注册时填的邮箱',
    email: '邮箱',
    submit: '发送重置链接',
    working: '正在发送…',
    // 措辞是刻意含糊的：不能因为「这个邮箱有没有注册过」而给出不同回答，
    // 否则这个页面就成了查人的工具
    done: '如果这个邮箱注册过，重置链接已经发出去了。检查一下收件箱（还有垃圾邮件）。',
    noEmailHint: '注册时没填邮箱的话，这条路走不通 —— 直接联系管理员帮你重置。',
  },

  reset: {
    title: '设置新口令',
    forAccount: (name: string) => `为账号「${name}」设置新口令`,
    checking: '正在核对链接…',
    invalid: '这个链接已经失效了。重新申请一次。',
    passphrase: '新口令（至少 8 位）',
    confirmPassphrase: '再输一遍新口令',
    submit: '设置并登录',
    working: '正在设置…',
    logoutNote: '设置成功后，这个账号在其他设备上的登录都会失效，需要用新口令重新登录。',
  },

  signup: {
    title: '开一个新账号',
    subtitle: '一个人用就够，需要时再邀请伴侣',
    householdName: '起个名字（如「我的记录」）',
    /** 用户没填名字时兜底 */
    defaultHouseholdName: '我们家',
    displayName: '你的昵称',
    email: '邮箱（选填）',
    emailHint: '只用于忘记口令时收重置链接。不填也能正常用，但就只能联系管理员人工重置了。',
    roleQuestion: '谁来记录周期？',
    roleSelf: '我自己',
    rolePartner: '我的另一半',
    roleHint: '只决定打开 App 先看到哪一页。邀请伴侣之后，双方都能记录和查看全部内容',
    passphrase: '口令（至少 8 位）',
    confirmPassphrase: '再输一遍口令',
    submit: '创建账号',
    working: '正在创建…',
    warning: '口令一旦忘记，数据就找不回来了。建议存进密码管理器。',
  },

  join: {
    title: '加入',
    subtitle: '填一个只属于你自己的登录名和口令',
    invitedBy: (who: string, home: string) => `${who} 邀请你加入「${home}」`,
    code: '邀请码',
    codeHint: '8 位，不区分大小写',
    codeLength: (n: number) => `邀请码要 ${n} 位`,
    checking: '正在核对邀请码…',
    submit: '加入',
    working: '正在加入…',
    sameDataHint: '加入后你们看到的是同一份记录，双方都能修改',
  },

  plan: {
    title: '席位',
    members: '成员',
    you: '你',
    solo: '单人',
    couple: '双人',
    seatsUsed: (used: number, total: number) => `${used} / ${total} 个席位`,
    roleTracker: '记录周期',
    rolePartner: '伴侣',
    invitePartner: '邀请另一半',
    inviteCreate: '生成邀请码',
    inviteActive: '邀请码',
    inviteExpires: (d: string) => `${d} 前有效`,
    inviteCopy: '复制',
    inviteCopied: '已复制',
    inviteRevoke: '作废',
    inviteHint: '把这个码发给对方，让 TA 在登录页选「收到了邀请码」填进去',
    seatsFull: '席位已满',
    sameDataNote: '你们共用同一份记录，双方都能修改',
    locked: '解锁双人',
    lockedHint: '免费账号有 1 个席位。解锁第二个席位后，就能邀请另一半共用同一份记录。',
    redeemTitle: '输入兑换码',
    redeemPlaceholder: '兑换码',
    redeemSubmit: '兑换',
    redeemWorking: '正在兑换…',
    redeemDone: '兑换成功，现在可以邀请另一半了',
    howToBuy: '需要兑换码？',
  },

  danger: {
    title: '危险操作',
    deleteAccount: '注销账号',
    deleteHintLast: '你是这里最后一个成员，注销会连同全部记录一起永久删除。',
    deleteHintShared: '注销只会删掉你自己的账号，共同的记录会留给对方。',
    deleteConfirmLabel: (name: string) => `输入 ${name} 确认`,
    deleteSubmit: '永久删除',
    deleteWorking: '正在删除…',
    deleteMismatch: '登录名不匹配',
  },

  phase: {
    menstrual: '经期',
    follicular: '卵泡期',
    fertile: '易孕期',
    ovulation: '排卵日',
    luteal: '黄体期',
    pms: '经前期',
    late: '经期推迟',
    unknown: '还没有数据',
  } satisfies Record<Phase, string>,

  phaseHint: {
    menstrual: '好好休息，多喝热水',
    follicular: '身体在回升，状态会越来越好',
    fertile: '这几天怀孕几率最高',
    ovulation: '今天是预计的排卵日',
    luteal: '身体在为下次经期做准备',
    pms: '可能会情绪低落、容易累，多担待',
    late: '比预计晚了，先别紧张，压力和作息都会影响',
    unknown: '记录几次经期之后就能开始预测了',
  } satisfies Record<Phase, string>,

  regularity: {
    unknown: '数据不足',
    high: '很规律',
    medium: '比较规律',
    low: '波动较大',
    irregular: '不太规律',
  } satisfies Record<Regularity, string>,

  flow: {
    spot: '点滴',
    light: '少量',
    medium: '中等',
    heavy: '较多',
  } satisfies Record<Flow, string>,

  protection: {
    none: '未避孕',
    condom: '避孕套',
    other: '其他方式',
  } satisfies Record<Protection, string>,

  symptom: {
    cramps: '痛经',
    headache: '头痛',
    backache: '腰酸',
    breastTender: '乳房胀痛',
    bloating: '腹胀',
    acne: '长痘',
    nausea: '恶心',
    insomnia: '失眠',
    appetite: '食欲变化',
    discharge: '分泌物',
  } satisfies Record<SymptomKey, string>,

  mood: ['很差', '不太好', '还行', '不错', '很好'],
  energy: ['很累', '有点累', '一般', '有精神', '精力充沛'],

  home: {
    greeting: (name: string, date: string) => `${name}，${date}`,
    cycleDay: (n: number) => `周期第 ${n} 天`,
    /** 跟在数字后面的单位。英文要区分单复数，所以做成函数 */
    dayUnit: (_n: number) => '天',
    phaseAria: (phase: string, cycleDay: string) => `${phase}，${cycleDay}`,
    untilPeriod: (n: number) => (n === 0 ? '预计今天来' : `距离例假还有 ${n} 天`),
    lateBy: (n: number) => `比预计晚了 ${n} 天`,
    expectedRange: (a: string, b: string) => `预计 ${a} 到 ${b} 之间`,
    fertileRange: (a: string, b: string) => `易孕期 ${a} — ${b}`,
    fertileFrom: (d: string) => `${d} 起`,
    ovulationOn: (d: string) => `预计 ${d} 排卵`,
    logToday: '记录今天',
    editToday: '修改今天的记录',
    noData: '还没有任何记录',
    noDataHint: '先记一次经期，预测就会开始工作',
    startPeriod: '记录经期开始',
    todaySummary: '今天已记录',
  },

  log: {
    title: (d: string) => `${d} 的记录`,
    flow: '经期流量',
    flowNone: '没有',
    symptoms: '身体感受',
    mood: '心情',
    energy: '精力',
    intimacy: '夫妻生活',
    intimacyNone: '没有',
    note: '备注',
    notePlaceholder: '想记点什么…',
    save: '保存',
    saving: '保存中…',
    clear: '清空这天',
    close: '关闭',
  },

  calendar: {
    /** 顺序跟着 meta.weekStart 走 */
    weekdays: ['一', '二', '三', '四', '五', '六', '日'],
    months: [
      '1月', '2月', '3月', '4月', '5月', '6月',
      '7月', '8月', '9月', '10月', '11月', '12月',
    ],
    title: (y: number, m: number) => `${y} 年 ${m} 月`,
    prevMonth: '上个月',
    nextMonth: '下个月',
    legendPeriod: '经期',
    legendPredicted: '预测经期',
    legendFertile: '易孕期',
    legendOvulation: '排卵日',
    legendIntimacy: '亲密',
    today: '今天',
    backToToday: '回到今天',
  },

  stats: {
    title: '统计',
    cycleLength: '周期长度',
    periodLength: '经期长度',
    days: (n: number | string) => `${n} 天`,
    avgWithSd: (m: string, sd: string) => `平均 ${m} 天，波动 ±${sd} 天`,
    regularityLabel: '规律程度',
    recentCycles: '最近的周期',
    noEnoughData: '记录满两个周期后，这里会显示趋势',
    anomalous: '异常',
    insightsTitle: '你的身体规律',
    insightsEmpty: '多记几次身体感受，这里会总结出你的规律',
    insightLine: (s: string, d: number) => `${s} 通常出现在周期第 ${d} 天前后`,
    insightBefore: (s: string, d: number) => `${s} 通常在经期前 ${d} 天出现`,
    intimacyCount: '夫妻生活',
    timesThisCycle: (n: number) => `本周期 ${n} 次`,
  },

  us: {
    title: '我们',
    /** 记录方没有昵称时的兜底称呼 */
    trackerFallback: 'TA',
    statusOf: (name: string) => `${name} 今天的状态`,
    tips: '给你的提示',
    timeline: '最近的记录',
    noRecords: '还没有记录',
    daysAgo: (n: number) => (n === 0 ? '今天' : n === 1 ? '昨天' : `${n} 天前`),
    tipPms: '这几天 TA 可能会比较累、容易烦躁。少讲道理，多倒杯热水。',
    tipMenstrual: (d: number) => `经期第 ${d} 天。别安排太累的行程，红糖姜茶备一份。`,
    tipFertile: '正处在易孕期，如果在备孕，这几天最关键。',
    tipOvulation: '今天是预计排卵日。',
    tipLate: '经期比预计晚了几天。先别急着下结论，压力、熬夜、生病都会推迟。',
    tipNormal: '状态平稳，没什么特别需要注意的。',
  },

  settings: {
    title: '设置',
    household: '基本',
    householdName: '名称',
    timezone: '时区',
    timezoneHint: '决定「今天」从几点开始算',
    language: '语言',
    languageHint: '只影响你自己看到的界面，同一个家庭里两个人可以各用各的',
    defaults: '默认值',
    defaultCycle: '默认周期长度',
    defaultPeriod: '默认经期长度',
    defaultsHint: '记录还不够时用它来兜底，有了历史数据就会自动被真实规律取代',
    account: '账号',
    email: '邮箱',
    emailNotSet: '未设置',
    emailEdit: '修改',
    emailAdd: '添加',
    emailSheetTitle: '修改邮箱',
    emailPlaceholder: '邮箱（留空表示不用邮箱）',
    emailWhy: '唯一的用处是你忘记口令时收重置链接。除此之外我们不会给你发任何邮件。',
    emailNeedsPassword: '改邮箱要先验证当前口令 —— 否则别人拿到你没锁屏的手机，改掉邮箱就能走「忘记口令」把账号接管走。',
    emailClearWarning: '清空邮箱之后就没法自助找回口令了，忘记口令只能联系管理员人工重置。',
    emailSaved: '邮箱已更新',
    emailCleared: '邮箱已清空',
    emailRecoveryOff: '管理员还没开通发信，暂时填了也收不到重置邮件。',
    changePassword: '修改口令',
    changePasswordAction: '修改',
    currentPassword: '当前口令',
    newPassword: '新口令（至少 8 位）',
    confirmPassword: '确认新口令',
    passwordChanged: '口令已修改，其他设备需要重新登录',
    passwordMismatch: '两次输入的新口令不一致',
    passwordTooShort: '新口令至少 8 位',
    passwordNote: (iterations: string) =>
      `口令会在这台设备上经过 ${iterations} 次加密运算后才发送，服务器从头到尾看不到你的明文口令。`,
    data: '数据',
    exportData: '导出全部数据',
    exportAction: '导出',
    exportHint: '导出为 JSON 文件，随时可以备份到自己电脑上',
    advanced: '进阶记录',
    trackBbt: '基础体温（BBT）',
    trackBbtHint: '每天早晨测体温，可以反推真实排卵日，是提升预测精度最有效的一项',
    trackLh: '排卵试纸（LH）',
    trackLhHint: '记录试纸强阳，可以直接锁定排卵日',
    logout: '退出登录',
    logoutAction: '退出',
    save: '保存',
    saved: '已保存',
    working: '处理中…',
    footer: '记录只有你和你邀请的人能看到，随时可以导出',
    disclaimerTitle: '重要提醒',
    disclaimer:
      '本 App 的排卵期和易孕期都是根据历史记录推算出来的，存在误差，不能作为避孕依据。身体出现异常请及时就医。',
  },

  common: {
    cancel: '取消',
    confirm: '确定',
    close: '关闭',
    increase: '增加',
    decrease: '减少',
    loading: '加载中…',
    retry: '重试',
    error: '出错了',
    networkError: '网络不太好，稍后再试',
    sessionExpired: '请重新登录',
    unexpectedResponse: '服务器返回了预期之外的内容',
    serverError: (status: number) => `服务器出错了（${status}）`,
    genericError: (status: number) => `出错了（${status}）`,
    exportFailed: '导出失败',
  },
}
